import React, {useState, useEffect, useMemo, useCallback} from "react";
import {Row, Col, notification, Skeleton} from "antd";
import Auxiliary from "util/Auxiliary";
import Widget from "components/Widget/index";
import {MailOutlined, MessageOutlined, BellOutlined} from "@ant-design/icons";
import LineIndicator from "./LineIndicator";
import {
    Bar,
    BarChart,
    Area,
    AreaChart,
    CartesianGrid,
    XAxis,
    YAxis,
    ResponsiveContainer,
    Tooltip
} from "recharts";
import axios from "../../../util/Api";
import Config from "../../../util/config";
import {CisUI} from "../../../util/CISUI";
import {Link} from "react-router-dom";
import Metrics from "../../../components/Metrics";
import CanvasJSReact from "./../../canvasjs/canvasjs.react";
import IntlMessages from "../../../util/IntlMessages";

const CanvasJS = CanvasJSReact.CanvasJS;
const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const AdminDashboard = () => {
    const userLocal = localStorage.getItem("userData") || "";

    let user = {
        id: 0,
        name: ""
    };

    if (userLocal !== "") {
        try {
            user = JSON.parse(userLocal);
        } catch (e) {
            user = {
                id: 0,
                name: ""
            };
        }
    }

    const [loading, setLoading] = useState(true);
    const [incomeExpense, setInocmeExpnese] = useState([]);

    const [state, setState] = useState({
        totalMember: 0,
        totalPayingMember: 0,
        totalNonPayingMember: 0,
        payingPercent: 0,
        nonPayingPercent: 0,
        totalTithe: 0,
        titheCompare: [],
        currentMonthCollection: {
            tithe: 0,
            mission: 0,
            others: 0,
            total: 0
        },
        previousMonthCollection: {
            tithe: 0,
            mission: 0,
            others: 0,
            total: 0
        },
        thisyearDonation: [],
        grossTithe: 0,
        allowableExpense: 0,
        netTithe: 0,
        devFund: 0,
        devFundTitle: "",
        kpi: [],
        bankReconcile: [],
        highestTithe: {
            name: "",
            amount: 0
        },
        highestMission: {
            name: "",
            amount: 0
        },
        titheByRegion: [],
        incomeByRestriction: [],
        cashBalance: 0,
        exenseYT: 0,
        receiveableBalance: 0,
        payableBalance: 0,
        notification: {
            notification: 0,
            bill: 0,
            po: 0,
            task: 0
        }
    });

    useEffect(() => {
        CanvasJS.addColorSet("brandedset", [
            "#2F4F4F",
            "#008080",
            "#2E8B57",
            "#3CB371",
            "#90EE90"
        ]);
    }, []);

    const getDashboardData = useCallback(() => {
        setLoading(true);

        axios
            .get(Config.apiserver + "dashboard", null, CisUI().HeaderRequest)
            .then((res) => {
                if (res.data.status === 1) {
                    const totalMember = Number(res.data.totalMember || 0);
                    const totalPayingMember = Number(res.data.totalPayingMember || 0);
                    const totalNonPayingMember = Number(res.data.totalNonPayingMember || 0);

                    const payingPercent =
                        totalMember > 0
                            ? ((totalPayingMember * 100) / totalMember).toFixed(2)
                            : 0;

                    const nonPayingPercent =
                        totalMember > 0
                            ? ((totalNonPayingMember * 100) / totalMember).toFixed(2)
                            : 0;

                    setInocmeExpnese(res.data.incomeExpense || []);

                    setState((prev) => ({
                        ...prev,
                        totalMember,
                        totalPayingMember,
                        payingPercent,
                        totalNonPayingMember,
                        nonPayingPercent,
                        totalTithe: res.data.totalTithe || 0,
                        titheCompare: res.data.titheCompare || [],
                        currentMonthCollection:
                            res.data.currentMonthCollection || prev.currentMonthCollection,
                        previousMonthCollection:
                            res.data.previousMonthCollection || prev.previousMonthCollection,
                        thisyearDonation: res.data.thisyearDonation || [],
                        grossTithe: res.data.grossTithe || 0,
                        allowableExpense: res.data.allowableExpense || 0,
                        netTithe: res.data.netTithe || 0,
                        devFund: res.data.devFund || 0,
                        devFundTitle: res.data.devFundTitle || "",
                        kpi: res.data.kpi || [],
                        bankReconcile: res.data.bankReconcile || [],
                        highestTithe: res.data.highestTithe || {
                            name: "",
                            amount: 0
                        },
                        highestMission: res.data.highestMission || {
                            name: "",
                            amount: 0
                        },
                        titheByRegion: res.data.titheByRegion || [],
                        incomeByRestriction: res.data.incomeByRestriction || [],
                        exenseYT: res.data.exenseYT || 0,
                        cashBalance: res.data.cashBalance || 0,
                        receiveableBalance: res.data.receiveableBalance || 0,
                        payableBalance: res.data.payableBalance || 0,
                        notification: res.data.notification || prev.notification
                    }));
                } else {
                    notification.warning({
                        message: "Alert",
                        description: res.data.msg
                    });
                }
            })
            .catch((errors) => {
                console.log(errors);
                notification.error({
                    message: "Dashboard Error",
                    description: "Failed to load dashboard data"
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        getDashboardData();
    }, [getDashboardData]);

    const colorPlates = useMemo(
        () => [
            "#142850",
            "#27496d",
            "#0c7b93",
            "#00a8cc",
            "#8566aa",
            "#6983aa",
            "#8ec6c5",
            "#4d4c7d",
            "#2c003e",
            "#7f78d2",
            "#1eb2a6"
        ],
        []
    );

    const titheByRegion = useMemo(
        () => ({
            animationEnabled: true,
            colorset: colorPlates,
            axisX: {
                title: ""
            },
            axisY: {
                title: "Amount"
            },
            toolTip: {
                shared: true
            },
            legend: {
                cursor: "pointer"
            },
            labelLine: false,
            theme: "light2",
            data: [
                {
                    type: "column",
                    name: "Tithe",
                    showInLegend: false,
                    color: "#00A8CCFF",
                    yValueFormatString: "$ #,##0.#",
                    dataPoints: state.titheByRegion || []
                }
            ]
        }),
        [colorPlates, state.titheByRegion]
    );

    const IncomeByRestriction = useMemo(
        () => ({
            animationEnabled: true,
            data: [
                {
                    type: "doughnut",
                    showInLegend: true,
                    indexLabel: "{name}: {y}",
                    yValueFormatString: "$#,###",
                    colorSet: "brandedset",
                    dataPoints: state.incomeByRestriction || []
                }
            ]
        }),
        [state.incomeByRestriction]
    );

    const formatCompact = useCallback((value) => {
        return new Intl.NumberFormat("en", {
            notation: "compact",
            compactDisplay: "short"
        }).format(value);
    }, []);

    const formatNumber = useCallback((value) => {
        return new Intl.NumberFormat("en").format(value);
    }, []);

    const renderSkeleton = () => (
        <>
            <style>{`
            .dashboard-shimmer {
                position: relative;
                overflow: hidden;
                background: #f4f7fb;
                border-radius: 8px;
            }

            .dashboard-shimmer::after {
                content: "";
                position: absolute;
                inset: 0;
                transform: translateX(-100%);
                background: linear-gradient(
                    90deg,
                    transparent,
                    rgba(255, 255, 255, 0.75),
                    transparent
                );
                animation: dashboardShimmer 1.4s infinite;
            }

            @keyframes dashboardShimmer {
                100% {
                    transform: translateX(100%);
                }
            }

            .sk-line {
                height: 14px;
                margin-bottom: 12px;
            }

            .sk-title {
                height: 26px;
                width: 45%;
                margin-bottom: 18px;
            }

            .sk-chart {
                height: 140px;
                margin-top: 14px;
            }

            .sk-card {
                height: 150px;
            }
        `}</style>

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <div className="gx-card">
                        <div className="gx-card-body">
                            <div className="dashboard-shimmer sk-title" />
                            <div className="dashboard-shimmer sk-line" style={{width: "70%"}} />
                            <div className="dashboard-shimmer sk-line" style={{width: "55%"}} />
                            <div className="dashboard-shimmer sk-chart" />
                        </div>
                    </div>
                </Col>

                {[1, 2, 3, 4, 5, 6].map((item) => (
                    <Col xl={8} lg={12} md={12} sm={24} xs={24} key={item}>
                        <div className="gx-card">
                            <div className="gx-card-body">
                                <div className="dashboard-shimmer sk-title" />
                                <div className="dashboard-shimmer sk-line" style={{width: "80%"}} />
                                <div className="dashboard-shimmer sk-line" style={{width: "65%"}} />
                                <div className="dashboard-shimmer sk-card" />
                            </div>
                        </div>
                    </Col>
                ))}

                {[1, 2].map((item) => (
                    <Col xl={12} lg={12} md={24} sm={24} xs={24} key={`wide-${item}`}>
                        <div className="gx-card">
                            <div className="gx-card-body">
                                <div className="dashboard-shimmer sk-title" />
                                <div className="dashboard-shimmer sk-line" style={{width: "90%"}} />
                                <div className="dashboard-shimmer sk-line" style={{width: "75%"}} />
                                <div className="dashboard-shimmer sk-chart" style={{height: 240}} />
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>
        </>
    );

    return (
        <Auxiliary>
            {loading ? (
                renderSkeleton()
            ) : (
                <Row>
                    <Col span={24}>
                        <div className="gx-card">
                            <div className="gx-card-body">
                                <Row>
                                    <Col xl={6} lg={12} md={12} sm={12} xs={24}>
                                        <div className="gx-wel-ema gx-pt-xl-2">
                                            <h1 className="gx-mb-3">
                                                <IntlMessages id={"Welcome"} /> {user.name}!
                                            </h1>

                                            <p className="gx-fs-sm gx-text-uppercase">
                                                <IntlMessages id={"You Have"} />
                                            </p>

                                            <ul className="gx-list-group">
                                                <li>
                                                    <MessageOutlined />
                                                    <span>
                                                        <Link to={"../vendor/pendingbill"}>
                                                            {state.notification.bill} Bill Approval request(s)
                                                        </Link>
                                                    </span>
                                                </li>

                                                <li>
                                                    <BellOutlined />
                                                    <span>
                                                        <Link to={"../user/notification"}>
                                                            {state.notification.notification} notifications
                                                        </Link>
                                                    </span>
                                                </li>

                                                <li>
                                                    <MailOutlined />
                                                    <span>
                                                        <Link to={"../purchase/pendingpo"}>
                                                            {state.notification.po} Pending PO&apos;s
                                                        </Link>
                                                    </span>
                                                </li>

                                                <li>
                                                    <MailOutlined />
                                                    <span>
                                                        <Link to={"../crm/tasklist"}>
                                                            {state.notification.task} Task
                                                        </Link>
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                    </Col>

                                    <Col
                                        xl={6}
                                        lg={12}
                                        md={12}
                                        sm={12}
                                        xs={24}
                                        className="gx-audi-col"
                                    >
                                        <div className="gx-site-dash gx-mb-2 gx-pt-3 gx-pt-sm-0 gx-pt-xl-2">
                                            <h6 className="gx-text-uppercase gx-mb-2 gx-mb-sm-4">
                                                <IntlMessages id={"Member Count"} />
                                            </h6>

                                            <ul className="gx-line-indicator">
                                                <li>
                                                    <LineIndicator
                                                        width="100%"
                                                        title="Total Adult Members"
                                                        color="cyan"
                                                        value={CisUI().getNumber(state.totalMember || 0)}
                                                    />
                                                </li>

                                                <li>
                                                    <LineIndicator
                                                        width={(state.payingPercent || 0) + "%"}
                                                        title={
                                                            "Total Paying Members (" +
                                                            CisUI().getNumber(state.totalPayingMember || 0) +
                                                            ")"
                                                        }
                                                        color="geekblue"
                                                        value={(state.payingPercent || 0) + "%"}
                                                    />
                                                </li>

                                                <li>
                                                    <LineIndicator
                                                        width={(state.nonPayingPercent || 0) + "%"}
                                                        title={
                                                            "Total Non-paying Members (" +
                                                            CisUI().getNumber(state.totalNonPayingMember || 0) +
                                                            ")"
                                                        }
                                                        color="orange"
                                                        value={(state.nonPayingPercent || 0) + "%"}
                                                    />
                                                </li>
                                            </ul>
                                        </div>
                                    </Col>

                                    <Col
                                        xl={12}
                                        lg={24}
                                        md={24}
                                        sm={24}
                                        xs={24}
                                        className="gx-visit-col"
                                    >
                                        <div className="gx-site-dash gx-pr-xl-5 gx-pt-3 gx-pt-xl-0 gx-pt-xl-2">
                                            <h6 className="gx-text-uppercase gx-mb-2 gx-mb-xl-4">
                                                <IntlMessages id={"Income & Expenditure"} />
                                            </h6>

                                            <ResponsiveContainer width="100%" height={140}>
                                                <AreaChart
                                                    data={incomeExpense}
                                                    margin={{top: 10, right: 30, left: 0, bottom: 0}}
                                                >
                                                    <defs>
                                                        <linearGradient
                                                            id="colorIncome"
                                                            x1="0"
                                                            y1="0"
                                                            x2="0"
                                                            y2="1"
                                                        >
                                                            <stop
                                                                offset="5%"
                                                                stopColor="#27496d"
                                                                stopOpacity={0.8}
                                                            />
                                                            <stop
                                                                offset="95%"
                                                                stopColor="#00a8cc"
                                                                stopOpacity={0.4}
                                                            />
                                                        </linearGradient>

                                                        <linearGradient
                                                            id="colorExpnese"
                                                            x1="0"
                                                            y1="0"
                                                            x2="0"
                                                            y2="1"
                                                        >
                                                            <stop
                                                                offset="5%"
                                                                stopColor="#bf163f"
                                                                stopOpacity={0.8}
                                                            />
                                                            <stop
                                                                offset="95%"
                                                                stopColor="#bf163f"
                                                                stopOpacity={0.4}
                                                            />
                                                        </linearGradient>
                                                    </defs>

                                                    <XAxis dataKey="name" />
                                                    <YAxis tickFormatter={formatCompact} />
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <Tooltip formatter={formatNumber} />

                                                    <Area
                                                        type="monotone"
                                                        dataKey="Income"
                                                        stroke="#8884d8"
                                                        fillOpacity={1}
                                                        fill="url(#colorIncome)"
                                                    />

                                                    <Area
                                                        type="monotone"
                                                        dataKey="Expense"
                                                        stroke="#82ca9d"
                                                        fillOpacity={1}
                                                        fill="url(#colorExpnese)"
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    </Col>

                    <Col xl={8} lg={24} md={8} sm={24} xs={24}>
                        <Metrics title={<IntlMessages id={"Total Gross Tithe"} />}>
                            <Row>
                                <Col xl={11} lg={12} md={24} sm={12} xs={12}>
                                    <h4 className="gx-mb-1">
                                        {CisUI().getCurrencyFormated1(state.totalTithe)}
                                    </h4>
                                    <p className="gx-mb-md-0 gx-text-light">
                                        <IntlMessages id={"Total Tithes YTD"} />
                                    </p>
                                </Col>

                                <Col xl={13} lg={12} md={24} sm={12} xs={12}>
                                    <ResponsiveContainer
                                        className="gx-barchart"
                                        width="100%"
                                        height={70}
                                    >
                                        <BarChart
                                            data={state.titheCompare}
                                            margin={{top: 0, right: 0, left: 0, bottom: 0}}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <Tooltip formatter={formatNumber} />
                                            <Bar
                                                dataKey="lastYear"
                                                stackId="a"
                                                fill="#27496d"
                                                barSize={8}
                                            />
                                            <Bar
                                                dataKey="thisYear"
                                                stackId="a"
                                                fill="#FE9E15"
                                                barSize={8}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Col>
                            </Row>
                        </Metrics>
                    </Col>

                    <Col xl={8} lg={12} md={8} sm={24} xs={24}>
                        <Metrics>
                            <div className="gx-customers">
                                <table className="table table-bordered">
                                    <thead>
                                    <tr>
                                        <th>
                                            <IntlMessages id={"Title"} />
                                        </th>
                                        <th className="currency">
                                            <IntlMessages id={"Previous Month"} />
                                        </th>
                                        <th className="currency">
                                            <IntlMessages id={"Current Month"} />
                                        </th>
                                        <th className="currency">
                                            <IntlMessages id={"Differences"} />
                                        </th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    <tr>
                                        <td>Net Tithe and Offering</td>
                                        <td className="currency">
                                            {CisUI().getCurrencyFormated1(
                                                state.previousMonthCollection.tithe
                                            )}
                                        </td>
                                        <td className="currency">
                                            {CisUI().getCurrencyFormated1(
                                                state.currentMonthCollection.tithe
                                            )}
                                        </td>
                                        <td className="currency">
                                            {CisUI().getCurrencyFormated1(
                                                state.previousMonthCollection.tithe -
                                                state.currentMonthCollection.tithe
                                            )}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td>Add: Missions Offering</td>
                                        <td className="currency">
                                            {CisUI().getCurrencyFormated1(
                                                state.previousMonthCollection.mission
                                            )}
                                        </td>
                                        <td className="currency">
                                            {CisUI().getCurrencyFormated1(
                                                state.currentMonthCollection.mission
                                            )}
                                        </td>
                                        <td className="currency">
                                            {CisUI().getCurrencyFormated1(
                                                state.previousMonthCollection.mission -
                                                state.currentMonthCollection.mission
                                            )}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td>Add: Other Contributions</td>
                                        <td className="currency">
                                            {CisUI().getCurrencyFormated1(
                                                state.previousMonthCollection.others
                                            )}
                                        </td>
                                        <td className="currency">
                                            {CisUI().getCurrencyFormated1(
                                                state.currentMonthCollection.others
                                            )}
                                        </td>
                                        <td className="currency">
                                            {CisUI().getCurrencyFormated1(
                                                state.previousMonthCollection.others -
                                                state.currentMonthCollection.others
                                            )}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td>Total</td>
                                        <td className="currency">
                                            {CisUI().getCurrencyFormated1(
                                                state.previousMonthCollection.total
                                            )}
                                        </td>
                                        <td className="currency">
                                            {CisUI().getCurrencyFormated1(
                                                state.currentMonthCollection.total
                                            )}
                                        </td>
                                        <td className="currency">
                                            {CisUI().getCurrencyFormated1(
                                                state.previousMonthCollection.total -
                                                state.currentMonthCollection.total
                                            )}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </Metrics>
                    </Col>

                    <Col xl={8} lg={12} md={8} sm={24} xs={24}>
                        <Metrics styleName={`gx-card-full`} title="This year : All Donations">
                            <Row>
                                <Col lg={24} md={24} sm={15} xs={15}>
                                    <ResponsiveContainer width="100%" height={103}>
                                        <AreaChart
                                            data={state.thisyearDonation}
                                            margin={{top: 0, right: 0, left: 0, bottom: 0}}
                                        >
                                            <defs>
                                                <linearGradient
                                                    id="color1"
                                                    x1="0"
                                                    y1="0"
                                                    x2="1"
                                                    y2="0"
                                                >
                                                    <stop
                                                        offset="5%"
                                                        stopColor="#27496d"
                                                        stopOpacity={0.9}
                                                    />
                                                    <stop
                                                        offset="95%"
                                                        stopColor="#00a8cc"
                                                        stopOpacity={0.9}
                                                    />
                                                </linearGradient>
                                            </defs>

                                            <XAxis dataKey="name" />
                                            <YAxis tickFormatter={formatCompact} />
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <Tooltip
                                                formatter={(value) =>
                                                    CisUI().getCurrencyFormated1(value)
                                                }
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="total"
                                                strokeWidth={0}
                                                stackId="2"
                                                stroke="#867AE5"
                                                fill="url(#color1)"
                                                fillOpacity={1}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </Col>
                            </Row>
                        </Metrics>
                    </Col>

                    <Col
                        xl={8}
                        lg={24}
                        md={24}
                        sm={24}
                        xs={24}
                        className="gx-order-sm-2"
                    >
                        <Row>
                            <Col xl={12} lg={12} md={12} sm={24} xs={24}>
                                <Widget styleName={`gx-card-full gx-p-3 gx-bg-white`}>
                                    <div className="gx-media gx-align-items-center gx-flex-nowrap">
                                        <div className="gx-media-body">
                                            <p>
                                                {CisUI().getUserInfo("comarea") === 6
                                                    ? "Net Income"
                                                    : "Highest Net Tithe"}
                                            </p>
                                            <h4
                                                className="gx-fs-xl text-center gx-font-weight-semi-bold gx-mb-1"
                                                style={{fontSize: "18px", color: "#00a8cc"}}
                                            >
                                                {CisUI().getCurrencyFormated1(
                                                    state.highestTithe.amount
                                                )}
                                            </h4>
                                            <p
                                                className="gx-mb-0 text-center"
                                                style={{color: "#27496d"}}
                                            >
                                                {state.highestTithe.name}
                                            </p>
                                        </div>
                                    </div>
                                </Widget>
                            </Col>

                            <Col xl={12} lg={12} md={12} sm={24} xs={24}>
                                <Widget styleName={`gx-card-full gx-p-3 gx-bg-white`}>
                                    <div className="gx-media gx-align-items-center gx-flex-nowrap">
                                        <div className="gx-media-body">
                                            <p>Highest Missions Offering</p>
                                            <h4
                                                className="gx-fs-xl text-center gx-font-weight-semi-bold gx-mb-1"
                                                style={{fontSize: "18px", color: "#00a8cc"}}
                                            >
                                                {CisUI().getCurrencyFormated1(
                                                    state.highestMission.amount
                                                )}
                                            </h4>
                                            <p
                                                className="text-center gx-mb-0"
                                                style={{color: "#27496d"}}
                                            >
                                                {state.highestMission.name}
                                            </p>
                                        </div>
                                    </div>
                                </Widget>
                            </Col>
                        </Row>

                        <div className="row">
                            <Widget title="Income by Restrictions" styleName="gx-card-tabs">
                                <CanvasJSChart options={IncomeByRestriction} />
                            </Widget>
                        </div>
                    </Col>

                    <Col
                        xl={16}
                        lg={24}
                        md={24}
                        sm={24}
                        xs={24}
                        className="gx-order-sm-1"
                    >
                        <Row>
                            <Col xl={6} lg={6} md={6} sm={12} xs={12}>
                                <Widget styleName={`gx-card-full gx-p-3 gx-bg-white`}>
                                    <div className="gx-media gx-align-items-center gx-flex-nowrap">
                                        <div className="gx-mr-2 gx-mr-xxl-3">
                                            <i
                                                className={`icon icon-diamond gx-fs-icon-lg`}
                                                style={{color: "#27496d"}}
                                            />
                                        </div>
                                        <div className="gx-media-body">
                                            <h4
                                                className="gx-fs-xl gx-font-weight-semi-bold gx-mb-1"
                                                style={{fontSize: "18px", color: "#00a8cc"}}
                                            >
                                                {CisUI().getCurrencyFormated1(state.grossTithe)}
                                            </h4>
                                            <p className="gx-mb-0" style={{color: "#27496d"}}>
                                                {CisUI().getUserInfo("comarea") <= 3
                                                    ? "Tithe Collected"
                                                    : CisUI().getUserInfo("comarea") === 4
                                                        ? "Gross Tithe from District"
                                                        : CisUI().getUserInfo("comarea") === 5
                                                            ? "Gross Tithe From Local"
                                                            : CisUI().getUserInfo("comarea") === 6
                                                                ? "Gross Tithe"
                                                                : ""}
                                            </p>
                                        </div>
                                    </div>
                                </Widget>
                            </Col>

                            <Col xl={6} lg={6} md={6} sm={12} xs={12}>
                                <Widget styleName={`gx-card-full gx-p-3 gx-bg-white`}>
                                    <div className="gx-media gx-align-items-center gx-flex-nowrap">
                                        <div className="gx-mr-2 gx-mr-xxl-3">
                                            <i
                                                className={`icon icon-tasks gx-fs-icon-lg`}
                                                style={{color: "#27496d"}}
                                            />
                                        </div>
                                        <div className="gx-media-body">
                                            <h4
                                                className="gx-fs-xl gx-font-weight-semi-bold gx-mb-1"
                                                style={{fontSize: "18px", color: "#00a8cc"}}
                                            >
                                                {CisUI().getCurrencyFormated1(
                                                    state.allowableExpense
                                                )}
                                            </h4>
                                            <p className="gx-mb-0" style={{color: "#27496d"}}>
                                                Allowable Expenses
                                            </p>
                                        </div>
                                    </div>
                                </Widget>
                            </Col>

                            <Col xl={6} lg={6} md={6} sm={12} xs={12}>
                                <Widget styleName={`gx-card-full gx-p-3 gx-bg-white`}>
                                    <div className="gx-media gx-align-items-center gx-flex-nowrap">
                                        <div className="gx-mr-2 gx-mr-xxl-3">
                                            <i
                                                className={`icon icon-team gx-fs-icon-lg`}
                                                style={{color: "#27496d"}}
                                            />
                                        </div>
                                        <div className="gx-media-body">
                                            <h4
                                                className="gx-fs-xl gx-font-weight-semi-bold gx-mb-1"
                                                style={{fontSize: "18px", color: "#00a8cc"}}
                                            >
                                                {CisUI().getCurrencyFormated1(state.netTithe)}
                                            </h4>
                                            <p className="gx-mb-0" style={{color: "#27496d"}}>
                                                {CisUI().getUserInfo("comarea") <= 3
                                                    ? "Net Tithe From Region"
                                                    : CisUI().getUserInfo("comarea") === 4
                                                        ? "Net to Nation"
                                                        : CisUI().getUserInfo("comarea") === 5
                                                            ? "Net to Region"
                                                            : CisUI().getUserInfo("comarea") === 6
                                                                ? "Net to District"
                                                                : ""}
                                            </p>
                                        </div>
                                    </div>
                                </Widget>
                            </Col>

                            <Col xl={6} lg={6} md={6} sm={12} xs={12}>
                                <Widget styleName={`gx-card-full gx-p-3 gx-bg-white`}>
                                    <div className="gx-media gx-align-items-center gx-flex-nowrap">
                                        <div className="gx-mr-2 gx-mr-xxl-3">
                                            <i
                                                className={`icon icon-files gx-fs-icon-lg`}
                                                style={{color: "#27496d"}}
                                            />
                                        </div>
                                        <div className="gx-media-body">
                                            <h4
                                                className="gx-fs-xl gx-font-weight-semi-bold gx-mb-1"
                                                style={{fontSize: "18px", color: "#00a8cc"}}
                                            >
                                                {CisUI().getCurrencyFormated1(state.devFund)}
                                            </h4>
                                            <p className="gx-mb-0" style={{color: "#27496d"}}>
                                                {state.devFundTitle}
                                            </p>
                                        </div>
                                    </div>
                                </Widget>
                            </Col>

                            {CisUI().getUserInfo("comarea") < 6 ? (
                                <Col xl={24} lg={24} md={24} sm={24} xs={24}>
                                    <Widget styleName={`gx-card-full gx-p-3 gx-bg-white`}>
                                        <h3>
                                            {CisUI().getUserInfo("comarea") <= 3
                                                ? "Current Month Tithe By Region"
                                                : CisUI().getUserInfo("comarea") === 4
                                                    ? "Current Month Tithe By District"
                                                    : CisUI().getUserInfo("comarea") === 5
                                                        ? "Current Month Tithe By Local"
                                                        : CisUI().getUserInfo("comarea") === 6
                                                            ? "Current Month Gross Tithe"
                                                            : ""}
                                        </h3>
                                        <CanvasJSChart options={titheByRegion} />
                                    </Widget>
                                </Col>
                            ) : (
                                ""
                            )}

                            <Col xl={24} lg={24} md={24} sm={24} xs={24}>
                                <Widget title="Current Financial Status">
                                    <div className="row">
                                        <div className="col-md-3">
                                            <Widget styleName={`gx-card-full gx-p-3 gx-bg-white`}>
                                                <div className="gx-media gx-align-items-center gx-flex-nowrap">
                                                    <div className="gx-media-body">
                                                        <h4
                                                            className="gx-fs-xl gx-font-weight-semi-bold gx-mb-1"
                                                            style={{
                                                                fontSize: "18px",
                                                                color: "#00a8cc"
                                                            }}
                                                        >
                                                            {CisUI().getCurrencyFormated1(
                                                                state.cashBalance
                                                            )}
                                                        </h4>
                                                        <p
                                                            className="gx-mb-0"
                                                            style={{color: "#27496d"}}
                                                        >
                                                            Cash Equ. Balance
                                                        </p>
                                                    </div>
                                                </div>
                                            </Widget>
                                        </div>

                                        <div className="col-md-3">
                                            <Widget styleName={`gx-card-full gx-p-3 gx-bg-white`}>
                                                <div className="gx-media gx-align-items-center gx-flex-nowrap">
                                                    <div className="gx-media-body">
                                                        <h4
                                                            className="gx-fs-xl gx-font-weight-semi-bold gx-mb-1"
                                                            style={{
                                                                fontSize: "18px",
                                                                color: "#00a8cc"
                                                            }}
                                                        >
                                                            {CisUI().getCurrencyFormated1(
                                                                state.exenseYT
                                                            )}
                                                        </h4>
                                                        <p
                                                            className="gx-mb-0"
                                                            style={{color: "#27496d"}}
                                                        >
                                                            Total Expenses
                                                        </p>
                                                    </div>
                                                </div>
                                            </Widget>
                                        </div>

                                        <div className="col-md-3">
                                            <Widget styleName={`gx-card-full gx-p-3 gx-bg-white`}>
                                                <div className="gx-media gx-align-items-center gx-flex-nowrap">
                                                    <div className="gx-media-body">
                                                        <h4
                                                            className="gx-fs-xl gx-font-weight-semi-bold gx-mb-1"
                                                            style={{
                                                                fontSize: "18px",
                                                                color: "#00a8cc"
                                                            }}
                                                        >
                                                            {CisUI().getCurrencyFormated1(
                                                                state.receiveableBalance
                                                            )}
                                                        </h4>
                                                        <p
                                                            className="gx-mb-0"
                                                            style={{color: "#27496d"}}
                                                        >
                                                            Accounts Receivable
                                                        </p>
                                                    </div>
                                                </div>
                                            </Widget>
                                        </div>

                                        <div className="col-md-3">
                                            <Widget styleName={`gx-card-full gx-p-3 gx-bg-white`}>
                                                <div className="gx-media gx-align-items-center gx-flex-nowrap">
                                                    <div className="gx-media-body">
                                                        <h4
                                                            className="gx-fs-xl gx-font-weight-semi-bold gx-mb-1"
                                                            style={{
                                                                fontSize: "18px",
                                                                color: "#00a8cc"
                                                            }}
                                                        >
                                                            {CisUI().getCurrencyFormated1(
                                                                state.payableBalance
                                                            )}
                                                        </h4>
                                                        <p
                                                            className="gx-mb-0"
                                                            style={{color: "#27496d"}}
                                                        >
                                                            Accounts Payable
                                                        </p>
                                                    </div>
                                                </div>
                                            </Widget>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-12">
                                            <table className="table table-bordered">
                                                <thead>
                                                <tr>
                                                    <th>
                                                        <IntlMessages id={"Indicator"} />
                                                    </th>
                                                    <th
                                                        style={{
                                                            textAlign: "right",
                                                            paddingRight: "10px"
                                                        }}
                                                    >
                                                        <IntlMessages id={"Current Month"} />
                                                    </th>
                                                    <th
                                                        style={{
                                                            textAlign: "right",
                                                            paddingRight: "10px"
                                                        }}
                                                    >
                                                        <IntlMessages id={"Previous Month"} />
                                                    </th>
                                                    <th
                                                        style={{
                                                            textAlign: "right",
                                                            paddingRight: "10px"
                                                        }}
                                                    >
                                                        <IntlMessages id={"Change"} />
                                                    </th>
                                                </tr>
                                                </thead>

                                                <tbody>
                                                {state.kpi.map((items, index) => {
                                                    return (
                                                        <tr key={index}>
                                                            <td>{items.name}</td>

                                                            <td
                                                                style={{
                                                                    textAlign: "right",
                                                                    paddingRight: "10px"
                                                                }}
                                                            >
                                                                {items.current > items.last ? (
                                                                    <i
                                                                        style={{color: "green"}}
                                                                        className="fas fa-caret-up"
                                                                    />
                                                                ) : items.current < items.last ? (
                                                                    <i
                                                                        style={{color: "red"}}
                                                                        className="fas fa-caret-down"
                                                                    />
                                                                ) : (
                                                                    ""
                                                                )}
                                                                {items.current}
                                                            </td>

                                                            <td
                                                                style={{
                                                                    textAlign: "right",
                                                                    paddingRight: "10px"
                                                                }}
                                                            >
                                                                {items.last}
                                                            </td>

                                                            <td
                                                                style={{
                                                                    textAlign: "right",
                                                                    paddingRight: "10px"
                                                                }}
                                                            >
                                                                {items.change > 0 ? (
                                                                    <i
                                                                        style={{color: "green"}}
                                                                        className="fas fa-caret-up"
                                                                    />
                                                                ) : items.change < 0 ? (
                                                                    <i
                                                                        style={{color: "red"}}
                                                                        className="fas fa-caret-down"
                                                                    />
                                                                ) : (
                                                                    ""
                                                                )}
                                                                {items.change}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </Widget>
                            </Col>

                            <Col xl={24} lg={24} md={24} sm={24} xs={24}>
                                <Widget title="Bank Reconciliation Summary">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <table className="table table-bordered">
                                                <thead>
                                                <tr>
                                                    <th>Account</th>
                                                    <th
                                                        style={{
                                                            textAlign: "right",
                                                            paddingRight: "10px"
                                                        }}
                                                    >
                                                        Account Balance
                                                    </th>
                                                    <th
                                                        style={{
                                                            textAlign: "right",
                                                            paddingRight: "10px"
                                                        }}
                                                    >
                                                        Last Reconcile Balance
                                                    </th>
                                                    <th
                                                        style={{
                                                            textAlign: "right",
                                                            paddingRight: "10px"
                                                        }}
                                                    >
                                                        Last Reconcile Date
                                                    </th>
                                                    <th
                                                        style={{
                                                            textAlign: "right",
                                                            paddingRight: "10px"
                                                        }}
                                                    >
                                                        Bank Balance
                                                    </th>
                                                    <th
                                                        style={{
                                                            textAlign: "right",
                                                            paddingRight: "10px"
                                                        }}
                                                    >
                                                        Balance as of
                                                    </th>
                                                    <th
                                                        style={{
                                                            textAlign: "right",
                                                            paddingRight: "10px"
                                                        }}
                                                    >
                                                        Items to Match
                                                    </th>
                                                </tr>
                                                </thead>

                                                <tbody>
                                                {state.bankReconcile.map((items, index) => {
                                                    return (
                                                        <tr key={index}>
                                                            <td>{items.name}</td>

                                                            <td
                                                                style={{
                                                                    textAlign: "right",
                                                                    paddingRight: "10px"
                                                                }}
                                                            >
                                                                {CisUI().getCurrencyFormated1(
                                                                    items.balance
                                                                )}
                                                            </td>

                                                            <td
                                                                style={{
                                                                    textAlign: "right",
                                                                    paddingRight: "10px"
                                                                }}
                                                            >
                                                                {CisUI().getCurrencyFormated1(
                                                                    items.lastBalance
                                                                )}
                                                            </td>

                                                            <td
                                                                style={{
                                                                    textAlign: "right",
                                                                    paddingRight: "10px"
                                                                }}
                                                            >
                                                                {items.lastDate !== ""
                                                                    ? CisUI().DateFormat(items.lastDate)
                                                                    : ""}
                                                            </td>

                                                            <td
                                                                style={{
                                                                    textAlign: "right",
                                                                    paddingRight: "10px"
                                                                }}
                                                            >
                                                                {CisUI().getCurrencyFormated1(
                                                                    items.bankBalance
                                                                )}
                                                            </td>

                                                            <td
                                                                style={{
                                                                    textAlign: "right",
                                                                    paddingRight: "10px"
                                                                }}
                                                            >
                                                                {items.balanceDate !== ""
                                                                    ? CisUI().DateFormat(items.balanceDate)
                                                                    : ""}
                                                            </td>

                                                            <td
                                                                style={{
                                                                    textAlign: "right",
                                                                    paddingRight: "10px"
                                                                }}
                                                            >
                                                                {CisUI().getCurrencyFormated1(
                                                                    items.itemToMatch
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </Widget>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            )}
        </Auxiliary>
    );
};

export default AdminDashboard;